import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { ISecureFile } from '@repo/toolkit/types'
import InnerBox from '@repo/ui/inner-box'
import { useMutation } from '@tanstack/react-query'
import { PropsWithChildren } from 'react'
import { toast } from 'sonner'

import { updateEnrollment } from '../../../../api/enrollments/update-enrollment'
import { useObserver } from '../../../../lib/contexts/observer'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { UpdateInput } from '../../../../lib/types/utils'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import { SecureProfileImageInput } from '../../../elements/inputs/secure-profile-image-input'

type Props = {
  enrollment: Pick<Enrollment, 'id'> & {
    secureIdentityPicture?: ISecureFile | null
  }
  maxImageSizeMB?: number
  disabled?: boolean
  pictureOnly?: boolean
  refetch?: () => void
}

const ClientEnrollmentProfileCard = ({
  enrollment,
  children,
  disabled,
  maxImageSizeMB = 16,
  pictureOnly = false,
  refetch
}: PropsWithChildren<Props>) => {
  const observer = useObserver({ key: 'update-enrollment-profile-client-personal-space' })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (input: UpdateInput<EnrollmentInput>) => updateEnrollment(input, ['id']),
    onError: showAxiosError,
    onSuccess: () => {
      observer.notify(['enrollment-client-personal-space'])
      observer.notify(['update-staff-progress-bar'])
      toast.success('Profil mis à jour avec succès')
      refetch?.()
    }
  })

  const profileImage = (
    <SecureProfileImageInput
      disabled={disabled}
      title="Photo du jeune"
      file={enrollment.secureIdentityPicture}
      toggleClassName="bg-secondaryRed-light"
      isLoading={isPending}
      maxImageSizeMB={maxImageSizeMB}
      iconColor={tailwindColors.secondaryRed.DEFAULT}
      onFileChange={async (file) => {
        await mutateAsync({ id: enrollment.id, secureIdentityPicture: { file } })
      }}
    />
  )

  if (pictureOnly) {
    return profileImage
  }

  return (
    <InnerBox className="rounded-lg p-4 border-primary-light-active bg-primary-light mobile:p-3">
      <div className="flex items-center gap-4 mobile:items-start">
        {profileImage}
        {children}
      </div>
    </InnerBox>
  )
}

export default ClientEnrollmentProfileCard
